(function () {
  "use strict";

  class AntihistamineDoseCalculator extends HTMLElement {
    connectedCallback() {
      if (this.__initialized) return;
      this.__initialized = true;

      var root = this;

      var state = {
        drug: "diphenhydramine",
        species: "dog",
        unit: "kg",
        weight: null,
        regimen: "standard",
        c1: false,
        c2: false
      };

      var lastSummary = "";
      var saveLabel = root.dataset.saveLabel || "Save to your pet’s profile";
      var saveHref = root.dataset.saveHref || "https://get.askavet.com";
      var saveTarget = root.dataset.saveTarget === "_blank" ? "_blank" : null;

      var DRUGS = {
        diphenhydramine: {
          label: "diphenhydramine",
          brands: ["Benadryl", "Nytol", "Unisom", "generic diphenhydramine"],
          tab: 25,
          tabLabel: "25 mg tablet",
          liq: 2.5,
          liqLabel: "children's 12.5 mg / 5 mL",
          drowsy: "Sedating",
          combo: "paracetamol or acetaminophen, ibuprofen, pseudoephedrine or phenylephrine, and it is not a “PM”, “-D”, “Sinus”, “Plus” or “Cold & Flu” version",
          note: "In some countries a product sold as “Benadryl” contains cetirizine or acrivastine, not diphenhydramine. Check the active ingredient, not the brand.",
          dog: function (kg) {
            return {
              mg: 2 * kg,
              high: 4 * kg,
              maxDoses: 3,
              basis: "2 mg/kg, start low. Range 2 to 4 mg/kg.",
              freq: "Every 8 to 12 hours",
              scaled: true
            };
          },
          cat: function (kg) {
            return {
              mg: 2 * kg,
              high: 4 * kg,
              maxDoses: 2,
              basis: "2 mg/kg. Range 2 to 4 mg/kg.",
              freq: "Every 12 hours",
              scaled: true,
              cat: true
            };
          }
        },

        cetirizine: {
          label: "cetirizine",
          brands: ["Zyrtec", "Reactine", "Zirtek", "Piriteze", "generic cetirizine"],
          tab: 10,
          tabLabel: "10 mg tablet",
          liq: 1,
          liqLabel: "1 mg / mL syrup",
          drowsy: "Minimal drowsiness",
          combo: "pseudoephedrine. Avoid any “-D” version such as Zyrtec-D",
          note: "",
          dog: function (kg, st) {
            if (st && st.regimen === "atopic") {
              var atopicMg = kg < 10 ? 5 : 10;

              return {
                mg: atopicMg,
                flat: true,
                maxDoses: 2,
                basis: "Escalated atopic regimen: " + atopicMg + " mg twice daily. For long-term allergic skin disease under veterinary direction.",
                freq: "Every 12 hours",
                note: "This twice-daily dose is for chronic atopic dermatitis managed by your vet. Do not use it for a single acute reaction."
              };
            }

            var mg = kg < 10 ? 5 : 10;

            return {
              mg: mg,
              flat: true,
              maxDoses: 1,
              basis: "Flat dose by size: 5 mg for smaller dogs and 10 mg for medium to large dogs.",
              freq: "Once daily"
            };
          },
          cat: function () {
            return {
              mg: 5,
              flat: true,
              maxDoses: 1,
              basis: "5 mg per cat.",
              freq: "Once daily",
              cat: true
            };
          }
        },

        loratadine: {
          label: "loratadine",
          brands: ["Claritin", "Claratyne", "Clarityn", "generic loratadine"],
          tab: 10,
          tabLabel: "10 mg tablet",
          liq: 1,
          liqLabel: "1 mg / mL syrup",
          drowsy: "Non-drowsy",
          combo: "pseudoephedrine. Avoid any “-D” version such as Claritin-D or Claratyne-D",
          note: "",
          dog: function (kg) {
            var mg = kg < 15 ? 5 : 10;

            return {
              mg: mg,
              flat: true,
              maxDoses: 1,
              basis: "Flat dose by size: 5 mg under 15 kg and 10 mg above.",
              freq: "Once daily"
            };
          },
          cat: function () {
            return {
              refer: true,
              msg: "<b>Loratadine is not routinely recommended in cats.</b> Feline data are limited. Ask your vet about cetirizine or chlorphenamine instead."
            };
          }
        },

        chlorphenamine: {
          label: "chlorphenamine",
          brands: ["Piriton", "Chlor-Trimeton", "generic chlorphenamine"],
          tab: 4,
          tabLabel: "4 mg tablet",
          liq: 0.4,
          liqLabel: "2 mg / 5 mL syrup",
          drowsy: "Sedating",
          combo: "paracetamol or acetaminophen, pseudoephedrine or phenylephrine. Many “Cold & Flu” tablets hide chlorphenamine alongside these",
          note: "Chlorphenamine is also spelled chlorpheniramine. Same drug.",
          dog: function (kg) {
            var low = 0.2 * kg;
            var high = Math.min(0.4 * kg, 8);

            return {
              mg: low,
              high: high,
              maxDoses: 3,
              basis: "0.2 to 0.4 mg/kg per dose, with a practical upper cap around 8 mg.",
              freq: "Every 8 to 12 hours",
              scaled: true
            };
          },
          cat: function () {
            return {
              mg: 1,
              high: 2,
              flat: true,
              maxDoses: 2,
              basis: "1 to 2 mg per cat.",
              freq: "Every 12 hours",
              cat: true
            };
          }
        },

        fexofenadine: {
          label: "fexofenadine",
          brands: ["Allegra", "Telfast", "generic fexofenadine"],
          tab: 180,
          tabLabel: "180 mg tablet",
          liq: 6,
          liqLabel: "30 mg / 5 mL liquid",
          drowsy: "Non-drowsy",
          combo: "pseudoephedrine. Never use Allegra-D or Telfast Decongestant",
          note: "Fruit juice can reduce fexofenadine absorption. Avoid grapefruit, apple and orange juice around dosing.",
          dog: function (kg) {
            var low = 5 * kg;
            var high = Math.min(10 * kg, 180);

            return {
              mg: low,
              high: high,
              maxDoses: 1,
              basis: "5 to 10 mg/kg once daily, with a practical cap around 180 mg.",
              freq: "Once daily",
              scaled: true,
              note: "Evidence for itch control is modest. Treat this as a monitored trial rather than a guaranteed response."
            };
          },
          cat: function () {
            return {
              refer: true,
              msg: "<b>Fexofenadine has limited feline dosing data.</b> Do not calculate a home dose from this tool. Confirm the drug and exact dose with your veterinarian."
            };
          }
        },

        clemastine: {
          label: "clemastine",
          brands: ["Tavegil", "Tavist", "generic clemastine"],
          tab: 1.34,
          tabLabel: "1.34 mg tablet",
          liq: 0.1,
          liqLabel: "0.5 mg / 5 mL liquid",
          drowsy: "Sedating",
          combo: "decongestants, paracetamol or acetaminophen, or any additional cold and flu ingredients",
          note: "Oral absorption in dogs is poor and variable, so response can be unreliable.",
          dog: function (kg) {
            var low = 0.05 * kg;
            var high = Math.min(0.1 * kg, 3);

            return {
              mg: low,
              high: high,
              maxDoses: 2,
              basis: "0.05 to 0.1 mg/kg every 12 hours, with a practical cap around 3 mg.",
              freq: "Every 12 hours",
              scaled: true,
              note: "Poor and variable oral absorption means some dogs show little clinical response."
            };
          },
          cat: function () {
            return {
              mg: 0.34,
              high: 0.68,
              flat: true,
              maxDoses: 2,
              basis: "0.34 to 0.68 mg per cat.",
              freq: "Every 12 hours",
              cat: true
            };
          }
        }
      };

      function $(id) {
        var map = {
          drug: "[data-adc-drug]",
          chips: "[data-adc-chips]",
          chipsCap: "[data-adc-chips-cap]",
          species: "[data-adc-species]",
          weight: "[data-adc-weight]",
          unit: "[data-adc-unit]",
          regimenField: "[data-adc-regimen-field]",
          regimen: "[data-adc-regimen]",
          "c1-wrap": "[data-adc-c1-wrap]",
          c1: "[data-adc-c1]",
          "c1-text": "[data-adc-c1-text]",
          "c2-wrap": "[data-adc-c2-wrap]",
          c2: "[data-adc-c2]",
          result: "[data-adc-result]",
          copyBtn: "[data-adc-copy-btn]",
          copybuffer: "[data-adc-copybuffer]"
        };

        return root.querySelector(map[id]);
      }

      function cap(value) {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }

      function fmt(number) {
        if (number === Math.round(number)) return String(Math.round(number));

        return number
          .toFixed(2)
          .replace(/0$/, "")
          .replace(/\.$/, "");
      }

      function q4(number) {
        return Math.round(number * 4) / 4;
      }

      function doseRangeText(result) {
        if (result.high && Math.round(result.high * 100) !== Math.round(result.mg * 100)) {
          return fmt(result.mg) + " to " + fmt(result.high) + " mg";
        }

        return fmt(result.mg) + " mg";
      }

      function doCopy(text, button) {
        function done() {
          button.textContent = "Copied";

          setTimeout(function () {
            button.textContent = "Copy dose";
          }, 1800);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(text)
            .then(done)
            .catch(function () {
              fallbackCopy(text, done);
            });

          return;
        }

        fallbackCopy(text, done);
      }

      function fallbackCopy(text, done) {
        var textarea = $("copybuffer");
        textarea.value = text;
        textarea.select();

        try {
          document.execCommand("copy");
          done();
        } catch (error) {
          // Clipboard access is not available in this browser context.
        }
      }

      function bindGroup(containerSelector, dataKey, extra) {
        var buttons = root.querySelectorAll(containerSelector + " button");

        buttons.forEach(function (button) {
          button.addEventListener("click", function () {
            state[dataKey] = button.dataset[dataKey];

            buttons.forEach(function (item) {
              item.setAttribute(
                "aria-pressed",
                item === button ? "true" : "false"
              );
            });

            if (extra) extra();
            render();
          });
        });
      }

      bindGroup("[data-adc-drug]", "drug", function () {
        syncDrug();
        updateRegimenVisibility();
      });

      bindGroup("[data-adc-species]", "species", updateRegimenVisibility);
      bindGroup("[data-adc-regimen]", "regimen");
      bindGroup("[data-adc-unit]", "unit");

      function updateRegimenVisibility() {
        var show =
          state.drug === "cetirizine" &&
          state.species === "dog";

        $("regimenField").style.display = show ? "" : "none";

        if (!show && state.regimen !== "standard") {
          state.regimen = "standard";

          root
            .querySelectorAll("[data-adc-regimen] button")
            .forEach(function (button) {
              button.setAttribute(
                "aria-pressed",
                button.dataset.regimen === "standard" ? "true" : "false"
              );
            });
        }
      }

      $("weight").addEventListener("input", function () {
        var value = parseFloat(this.value);
        state.weight = Number.isFinite(value) && value > 0 ? value : null;
        render();
      });

      function bindCheck(id, key, wrapperId) {
        $(id).addEventListener("change", function () {
          state[key] = this.checked;
          $(wrapperId).classList.toggle("on", this.checked);
          render();
        });
      }

      bindCheck("c1", "c1", "c1-wrap");
      bindCheck("c2", "c2", "c2-wrap");

      function syncDrug() {
        var drug = DRUGS[state.drug];

        $("chips").innerHTML = drug.brands
          .map(function (brand) {
            return '<span class="chip">' + brand + "</span>";
          })
          .join("");

        $("chipsCap").textContent =
          "Common brand names. Brands differ by country, so always check the active ingredient.";

        $("c1-text").innerHTML =
          "<b>It is plain " +
          drug.label +
          " only.</b> The box lists " +
          drug.label +
          " and nothing else. It does <b>not</b> contain " +
          drug.combo +
          ". Combination products can be fatal, especially to cats." +
          (drug.note ? " " + drug.note : "");

        state.c1 = false;
        $("c1").checked = false;
        $("c1-wrap").classList.remove("on");
      }

      function kilograms() {
        if (state.weight === null) return null;

        return state.unit === "kg"
          ? state.weight
          : state.weight / 2.2046226;
      }

      function render() {
        var resultHost = $("result");
        var weightKg = kilograms();
        var ready = weightKg !== null && state.c1 && state.c2;

        if (!ready) {
          var message = "Enter a weight and tick both gates to see the dose.";

          if (weightKg !== null && !(state.c1 && state.c2)) {
            message = "Confirm both safety gates above to reveal the dose.";
          } else if (weightKg === null && state.c1 && state.c2) {
            message = "Enter a weight to see the dose.";
          }

          resultHost.innerHTML =
            '<div class="result-locked">' + message + "</div>";

          return;
        }

        var hardBlock =
          state.species === "dog"
            ? weightKg > 120 || weightKg < 0.3
            : weightKg > 20 || weightKg < 0.3;

        if (hardBlock) {
          resultHost.innerHTML =
            '<div class="refer"><b>That weight looks off.</b> ' +
            fmt(Math.round(weightKg * 10) / 10) +
            " kg is outside the expected range for a " +
            state.species +
            ". Double-check the number and the kg/lb toggle before dosing.</div>";

          return;
        }

        var drug = DRUGS[state.drug];
        var result = drug[state.species](weightKg, state);

        if (result.refer) {
          resultHost.innerHTML =
            '<div class="refer">' + result.msg + "</div>";

          return;
        }

        var displayMg = result.mg;
        var tablets = q4(displayMg / drug.tab);
        var liquidMl = displayMg / drug.liq;
        var dailyMax = Math.round(displayMg * result.maxDoses);
        var flags = [];

        if (state.species === "dog" && weightKg > 90) {
          flags.push(
            "Large for a dog. Double-check the weight and confirm the dose with your vet."
          );
        }

        if (state.species === "cat" && weightKg > 12) {
          flags.push("Large for a cat. Double-check the weight.");
        }

        if (result.scaled && weightKg < 2) {
          flags.push(
            "Very small pet. Use a liquid formulation for accurate measuring and confirm the first dose with your vet."
          );
        }

        if (
          result.flat &&
          (
            (state.species === "dog" && weightKg < 2) ||
            (state.species === "cat" && weightKg < 1.5)
          )
        ) {
          flags.push(
            "Very small or possibly young animal on a fixed dose. Confirm the dose with your vet before giving it."
          );
        }

        if (tablets < 0.25) {
          flags.push(
            "The tablet fraction is too small to split safely. Use a suitable liquid instead."
          );
        }

        if (result.note) flags.push(result.note);

        if (result.cat) {
          flags.push(
            "Cat dose: confirm with your vet before the first use and make certain the product is single ingredient."
          );
        }

        var tabletText =
          tablets < 0.25
            ? "use liquid"
            : fmt(tablets) + " × " + drug.tab + " mg tablet";

        var tabletDisplay =
          tablets < 0.25
            ? '<span class="v" style="font-size:18px">use liquid</span>'
            : '<span class="v">' +
              fmt(tablets) +
              ' <small>&times; ' +
              drug.tab +
              " mg</small></span>";

        var rangeText = doseRangeText(result);

        var rangeLine =
          result.high
            ? "Typical range " + rangeText + ". Start at the low end unless your vet directs otherwise."
            : result.flat
              ? "Flat dose. See the calculation basis below."
              : "Weight-based dose. See the calculation basis below.";

        lastSummary =
          "ASK A VET™ antihistamine dose\n" +
          cap(drug.label) +
          ", " +
          state.species +
          ", " +
          fmt(Math.round(weightKg * 10) / 10) +
          " kg\n" +
          "Dose: " +
          rangeText +
          " per dose\n" +
          "Low-end tablet equivalent: " +
          tabletText +
          "\n" +
          "Low-end liquid equivalent: " +
          fmt(Math.round(liquidMl * 10) / 10) +
          " mL of " +
          drug.liqLabel +
          "\n" +
          "Frequency: " +
          result.freq +
          "\n" +
          "General guidance only. Confirm with your vet before dosing.";

        var html =
          '<div class="dose-hero">' +
            '<div class="lbl">' +
              drug.label +
              " &middot; " +
              state.species +
              " &middot; per dose" +
            "</div>" +
            '<div class="big">' +
              rangeText +
            "</div>" +
            '<div class="range">' +
              rangeLine +
            "</div>" +
            '<span class="badge">' +
              drug.drowsy +
            "</span>" +
          "</div>" +

          '<div class="forms">' +
            '<div class="form-card">' +
              '<div class="t">Low-end tablet equivalent</div>' +
              tabletDisplay +
              '<div class="sub">' +
                drug.tabLabel +
              "</div>" +
            "</div>" +

            '<div class="form-card">' +
              '<div class="t">Low-end liquid equivalent</div>' +
              '<span class="v">' +
                fmt(Math.round(liquidMl * 10) / 10) +
                " <small>mL</small>" +
              "</span>" +
              '<div class="sub">' +
                drug.liqLabel +
              "</div>" +
            "</div>" +
          "</div>" +

          '<div class="freq">' +
            "<div>Frequency<b>" +
              result.freq +
            "</b></div>" +
            "<div>Low-end max / 24 h<b>" +
              dailyMax +
              " mg</b></div>" +
            "<div>Based on<b>" +
              fmt(Math.round(weightKg * 10) / 10) +
              " kg</b></div>" +
          "</div>" +

          '<div class="basis"><b>Basis:</b> ' +
            result.basis +
          "</div>";

        flags.forEach(function (flag) {
          html +=
            '<div class="flag">' +
              '<span class="ic">!</span>' +
              "<div>" +
                flag +
              "</div>" +
            "</div>";
        });

        html +=
          '<div class="actions">' +
            '<button type="button" class="btn" data-adc-copy-btn>Copy dose</button>' +
            '<a class="btn btn-primary" data-adc-save-btn href="' +
              saveHref +
              '"' +
              (
                saveTarget
                  ? ' target="' + saveTarget + '" rel="noopener"'
                  : ""
              ) +
            ">" +
              saveLabel +
            "</a>" +
          "</div>" +

          '<div class="actions-cap">' +
            "Keep a record of what you gave, when, and how your pet reacted in ASK A VET™." +
          "</div>";

        resultHost.innerHTML = html;

        var copyButton = $("copyBtn");

        if (copyButton) {
          copyButton.addEventListener("click", function () {
            doCopy(lastSummary, copyButton);
          });
        }
      }

      syncDrug();
      updateRegimenVisibility();
      render();
    }
  }

  if (!customElements.get("s-antihistamine-dose-calculator")) {
    customElements.define(
      "s-antihistamine-dose-calculator",
      AntihistamineDoseCalculator
    );
  }
})();
